// Context sheet for "Remix in Audiotool". Explains what happens, offers to
// open the Audiotool hub page where they connect and export.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { audiotoolHubPath } from "@/lib/ctas";

export function AudiotoolInterstitial({
  seedId,
  seedTitle,
  producer,
  trackCount,
  onClose,
}: {
  seedId: string;
  /** Absent → "Opening in Audiotool", never "this seed". */
  seedTitle?: string;
  producer?: string;
  trackCount?: number;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const [step, setStep] = useState<"main" | "what">("main");
  const byline = seedByline(trackCount, producer);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  return (
    <>
      <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed inset-0 z-[60] flex items-center justify-center">
        <div className="relative mx-4 w-full max-w-sm rounded-2xl border border-white/10 bg-[#141419] p-6 shadow-[0_20px_60px_rgba(0,0,0,0.6)]">

          {step === "main" && (
            <>
              <div className="flex items-center gap-3">
                <div className="size-11 rounded-xl bg-[#0d0d12] border border-white/10 flex items-center justify-center shrink-0 overflow-hidden">
                  <img src="/audiotool-icon.webp" alt="" className="size-7 rounded" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-base font-bold">
                    {seedTitle ? (
                      <>Opening <span className="break-words">{seedTitle}</span> in Audiotool</>
                    ) : (
                      "Opening in Audiotool"
                    )}
                  </h3>
                  {byline && <p className="text-[11px] text-muted-foreground truncate">{byline}</p>}
                </div>
              </div>

              <p className="mt-4 text-sm text-muted-foreground">
                A new Audiotool project, every stem on its own track, ready to remix in the browser.
              </p>

              <div className="mt-4 flex flex-col gap-2">
                <button
                  type="button"
                  onClick={() => { onClose(); navigate(audiotoolHubPath(seedId)); }}
                  className="h-11 rounded-xl bg-[#7c5cbf] text-white font-bold text-sm flex items-center justify-center gap-2 hover:bg-[#9171d4] transition-colors"
                >
                  Open Audiotool Hub
                  <svg className="size-3.5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M7 17L17 7M9 7h8v8" /></svg>
                </button>
                <button
                  type="button"
                  onClick={() => setStep("what")}
                  className="text-xs text-muted-foreground/60 mt-1 text-center"
                >
                  What is Audiotool?
                </button>
              </div>

              <button type="button" onClick={onClose} className="mt-3 w-full text-center text-xs text-muted-foreground/50">
                Cancel
              </button>
            </>
          )}

          {step === "what" && (
            <>
              <div className="flex items-center gap-3">
                <div className="size-11 rounded-xl bg-[#0d0d12] border border-white/10 flex items-center justify-center shrink-0 overflow-hidden">
                  <img src="/audiotool-icon.webp" alt="" className="size-7 rounded" />
                </div>
                <div>
                  <h3 className="text-base font-bold">Audiotool</h3>
                  <p className="text-[11px] text-muted-foreground">Free, runs in any browser</p>
                </div>
              </div>

              <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
                Audiotool is a free browser-based DAW. Seedbed can open any seed as an Audiotool project with all its stems on separate tracks. No download, no install.
              </p>

              <div className="mt-4 flex flex-col gap-2">
                <button
                  type="button"
                  onClick={() => { onClose(); navigate(audiotoolHubPath(seedId)); }}
                  className="h-11 rounded-xl bg-[#7c5cbf] text-white font-bold text-sm flex items-center justify-center gap-2 hover:bg-[#9171d4] transition-colors"
                >
                  {seedTitle ? `Try it with ${seedTitle}` : "Try it"}
                </button>
              </div>

              <button type="button" onClick={() => setStep("main")} className="mt-3 w-full text-center text-xs text-muted-foreground">
                ← Back
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
}

/** "4 tracks by mia", "4 tracks", "by mia" — or nothing, never "0 tracks by ". */
export function seedByline(trackCount: number | undefined, producer: string | undefined): string | null {
  const tracks = trackCount ? `${trackCount} ${trackCount === 1 ? "track" : "tracks"}` : "";
  const by = producer ? `by ${producer}` : "";
  return [tracks, by].filter(Boolean).join(" ") || null;
}
